import React, { useState } from 'react';
import { Plus, Trash2, Grid3X3, Compass, Target } from 'lucide-react';
import { Resource } from '../../../types';

interface SpatialCanvasConfigFormProps {
  resource: Resource;
  config?: Record<string, any>;
  onConfigChange: (config: Record<string, any>) => void;
}

interface SpatialObject {
  id: string;
  type: string;
  position: string;
  color: string;
}

interface GridCell {
  x: number;
  y: number;
  position: string;
}

export const SpatialCanvasConfigForm: React.FC<SpatialCanvasConfigFormProps> = ({
  resource,
  config,
  onConfigChange
}) => {
  const mergedConfig = { ...resource, ...config };
  const [gridSize, setGridSize] = useState<string>(mergedConfig.gridSize || '5x5');
  const [objects, setObjects] = useState<SpatialObject[]>(mergedConfig.objects || []);
  const [directions, setDirections] = useState<string[]>(mergedConfig.directions || []);
  const [showGrid, setShowGrid] = useState<boolean>(mergedConfig.showGrid !== undefined ? mergedConfig.showGrid : true);
  const [allowInteraction, setAllowInteraction] = useState<boolean>(mergedConfig.allowInteraction !== undefined ? mergedConfig.allowInteraction : true);
  const [question, setQuestion] = useState<string>(mergedConfig.question || '');

  const availableShapes = ['star', 'circle', 'square', 'triangle', 'diamond', 'hexagon'];
  const availableColors = ['red', 'blue', 'green', 'yellow', 'purple', 'orange', 'pink', 'brown'];
  const availablePositions = ['center', 'left', 'right', 'top', 'bottom', 'top-left', 'top-right', 'bottom-left', 'bottom-right'];
  const availableDirections = ['devant', 'derrière', 'gauche', 'droite', 'haut', 'bas', 'centre', 'diagonale'];

  const gridSizes = ['3x3', '4x4', '5x5', '6x6', '7x7', '8x8'];

  // Remove the problematic useEffect that was causing infinite loops
  // Instead, we'll call onConfigChange only when explicitly needed

  const addObject = () => {
    const newObject: SpatialObject = {
      id: `obj_${Date.now()}`,
      type: 'star',
      position: 'center',
      color: 'red'
    };
    const newObjects = [...objects, newObject];
    setObjects(newObjects);
    onConfigChange({
      ...resource,
      objects: newObjects
    });
  };

  const updateObject = (index: number, field: keyof SpatialObject, value: any) => {
    const updatedObjects = [...objects];
    updatedObjects[index] = { ...updatedObjects[index], [field]: value };
    setObjects(updatedObjects);
    onConfigChange({
      ...resource,
      objects: updatedObjects
    });
  };

  const removeObject = (index: number) => {
    const newObjects = objects.filter((_, i) => i !== index);
    setObjects(newObjects);
    onConfigChange({
      ...resource,
      objects: newObjects
    });
  };

  const addDirection = () => {
    if (directions.length < availableDirections.length) {
      const newDirection = availableDirections.find(dir => !directions.includes(dir));
      if (newDirection) {
        const newDirections = [...directions, newDirection];
        setDirections(newDirections);
        onConfigChange({
          ...resource,
          directions: newDirections
        });
      }
    }
  };

  const removeDirection = (direction: string) => {
    const newDirections = directions.filter(d => d !== direction);
    setDirections(newDirections);
    onConfigChange({
      ...resource,
      directions: newDirections
    });
  };

  const getColorValue = (colorName: string) => {
    const colorMap: Record<string, string> = {
      'red': '#EF4444',
      'blue': '#3B82F6',
      'green': '#10B981',
      'yellow': '#F59E0B',
      'purple': '#8B5CF6',
      'orange': '#F97316',
      'pink': '#EC4899',
      'brown': '#A0522D'
    };
    return colorMap[colorName] || colorName;
  };

  const getShapeIcon = (shape: string) => {
    const iconMap: Record<string, string> = {
      'star': '⭐',
      'circle': '●',
      'square': '■',
      'triangle': '▲',
      'diamond': '◆',
      'hexagon': '⬡'
    };
    return iconMap[shape] || '⭐';
  };

  const getPositionLabel = (position: string) => {
    const positionMap: Record<string, string> = {
      'center': 'Centre',
      'left': 'Gauche',
      'right': 'Droite',
      'top': 'Haut',
      'bottom': 'Bas',
      'top-left': 'Haut-gauche',
      'top-right': 'Haut-droite',
      'bottom-left': 'Bas-gauche',
      'bottom-right': 'Bas-droite'
    };
    return positionMap[position] || position;
  };

  const generateGridPreview = () => {
    const [rows, cols] = gridSize.split('x').map(Number);
    const grid: GridCell[][] = [];
    
    for (let y = 0; y < rows; y++) {
      const row: GridCell[] = [];
      for (let x = 0; x < cols; x++) {
        const centerX = Math.floor(cols / 2);
        const centerY = Math.floor(rows / 2);
        
        let position = 'center';
        if (x < centerX) position = 'left';
        if (x > centerX) position = 'right';
        if (y < centerY) position = 'top';
        if (y > centerY) position = 'bottom';
        if (x < centerX && y < centerY) position = 'top-left';
        if (x > centerX && y < centerY) position = 'top-right';
        if (x < centerX && y > centerY) position = 'bottom-left';
        if (x > centerX && y > centerY) position = 'bottom-right';
        
        row.push({ x, y, position });
      }
      grid.push(row);
    }
    
    return grid;
  };

  const grid = generateGridPreview();

  return (
    <div className="space-y-6">
      {/* Question Configuration */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Question ou consigne
        </label>
        <textarea
          value={question}
          onChange={(e) => {
            const newQuestion = e.target.value;
            setQuestion(newQuestion);
            onConfigChange({
              ...resource,
              question: newQuestion
            });
          }}
          placeholder="Ex: Placez les objets selon les consignes spatiales données"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={3}
        />
      </div>

      {/* Grid Configuration */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 flex items-center mb-4">
          <Grid3X3 className="w-5 h-5 mr-2" />
          Configuration de la grille
        </h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Taille de la grille
            </label>
            <select
              value={gridSize}
              onChange={(e) => {
                const newGridSize = e.target.value;
                setGridSize(newGridSize);
                onConfigChange({
                  ...resource,
                  gridSize: newGridSize
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {gridSizes.map(size => (
                <option key={size} value={size}>
                  {size} ({size.split('x')[0]} × {size.split('x')[1]})
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Afficher la grille
            </label>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={showGrid}
                onChange={(e) => {
                  const newShowGrid = e.target.checked;
                  setShowGrid(newShowGrid);
                  onConfigChange({
                    ...resource,
                    showGrid: newShowGrid
                  });
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-600">
                {showGrid ? 'Oui' : 'Non'}
              </span>
            </div>
          </div>
        </div>

        {/* Grid Preview */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Aperçu de la grille
          </label>
          <div className="inline-block border-2 border-gray-300 rounded-lg p-2 bg-white">
            {grid.map((row, rowIndex) => (
              <div key={rowIndex} className="flex">
                {row.map((cell, colIndex) => (
                  <div
                    key={colIndex}
                    className="w-8 h-8 border border-gray-200 flex items-center justify-center text-xs text-gray-500 bg-gray-50"
                    title={`${cell.position} (${cell.x}, ${cell.y})`}
                  >
                    {cell.position === 'center' ? 'C' : 
                     cell.position === 'left' ? 'G' :
                     cell.position === 'right' ? 'D' :
                     cell.position === 'top' ? 'H' :
                     cell.position === 'bottom' ? 'B' : '•'}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            C = Centre, G = Gauche, D = Droite, H = Haut, B = Bas
          </p>
        </div>
      </div>

      {/* Objects Configuration */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Target className="w-5 h-5 mr-2" />
            Objets à placer
          </h3>
          <button
            type="button"
            onClick={addObject}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter un objet</span>
          </button>
        </div>

        <div className="space-y-3">
          {objects.map((obj, index) => (
            <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  Objet {index + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeObject(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {/* Shape */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Forme
                  </label>
                  <select
                    value={obj.type}
                    onChange={(e) => updateObject(index, 'type', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {availableShapes.map(shape => (
                      <option key={shape} value={shape}>
                        {getShapeIcon(shape)} {shape}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Position */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Position
                  </label>
                  <select
                    value={obj.position}
                    onChange={(e) => updateObject(index, 'position', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {availablePositions.map(position => (
                      <option key={position} value={position}>
                        {getPositionLabel(position)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Color */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Couleur
                  </label>
                  <select
                    value={obj.color}
                    onChange={(e) => updateObject(index, 'color', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {availableColors.map(color => (
                      <option key={color} value={color}>
                        <span 
                          className="inline-block w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: getColorValue(color) }}
                        ></span>
                        {color}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Preview */}
              <div className="mt-3 flex items-center space-x-2">
                <span className="text-xs text-gray-500">Aperçu:</span>
                <div
                  className="w-6 h-6 border border-gray-300 rounded flex items-center justify-center text-sm"
                  style={{ backgroundColor: getColorValue(obj.color) }}
                >
                  {getShapeIcon(obj.type)}
                </div>
                <span className="text-xs text-gray-600">
                  à placer {getPositionLabel(obj.position)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Directions Configuration */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Compass className="w-5 h-5 mr-2" />
            Directions spatiales
          </h3>
          <button
            type="button"
            onClick={addDirection}
            disabled={directions.length >= availableDirections.length}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter une direction</span>
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {availableDirections.map(direction => (
            <div
              key={direction}
              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                directions.includes(direction)
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300'
              }`}
              onClick={() => {
                if (directions.includes(direction)) {
                  removeDirection(direction);
                } else if (directions.length < 8) {
                  const newDirections = [...directions, direction];
                  setDirections(newDirections);
                  onConfigChange({
                    ...resource,
                    directions: newDirections
                  });
                }
              }}
            >
              <div className="text-center">
                <div className="text-lg mb-1">
                  {direction === 'devant' ? '⬆️' :
                   direction === 'derrière' ? '⬇️' :
                   direction === 'gauche' ? '⬅️' :
                   direction === 'droite' ? '➡️' :
                   direction === 'haut' ? '⬆️' :
                   direction === 'bas' ? '⬇️' :
                   direction === 'centre' ? '🎯' :
                   direction === 'diagonale' ? '↗️' : '•'}
                </div>
                <div className="text-xs font-medium capitalize">
                  {direction}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <p className="text-xs text-gray-500 mt-2">
          Cliquez sur une direction pour l'activer/désactiver
        </p>
      </div>

      {/* Settings */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Paramètres</h3>
        
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-gray-700">
              Permettre l'interaction
            </label>
            <p className="text-xs text-gray-500">
              Permettre aux élèves de placer les objets
            </p>
          </div>
          <input
            type="checkbox"
            checked={allowInteraction}
            onChange={(e) => {
              const newAllowInteraction = e.target.checked;
              setAllowInteraction(newAllowInteraction);
              onConfigChange({
                ...resource,
                allowInteraction: newAllowInteraction
              });
            }}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        </div>
      </div>

      {/* Summary */}
      <div className="p-4 bg-blue-50 rounded-lg">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Résumé de la configuration</h4>
        <div className="text-xs text-blue-800 space-y-1">
          <p>• Grille: {gridSize} ({gridSize.split('x')[0]} × {gridSize.split('x')[1]} cellules)</p>
          <p>• {objects.length} objet(s) à placer</p>
          <p>• {directions.length} direction(s) spatiale(s) activée(s)</p>
          {question && <p>• Question: "{question}"</p>}
        </div>
      </div>
    </div>
  );
};
